import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { MaterialIcon, useMaterialIcons } from "../../lib/materialIcons";
import { compressImage } from "../../lib/imageCompress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const CORE = [["panels", "Solar Panel"], ["inverters", "Inverter"], ["batteries", "Battery"], ["rafters", "Rafter"], ["purlins", "Purlin"]];

const IconRow = ({ k, label }) => {
  const { icons, reload } = useMaterialIcons();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("key", k); fd.append("file", await compressImage(file, 600, 0.85));
      await api.post("/material-icons", fd);
      await reload(); toast.success(`${label} image updated`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reset = async () => { await api.delete("/material-icons", { params: { key: k } }); await reload(); toast.success("Default symbol restored"); };
  const slug = k.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="flex items-center gap-3 py-2.5">
      <MaterialIcon iconKey={k} size={44} />
      <div className="flex-1 min-w-0 text-sm font-semibold truncate">{label}</div>
      {icons[k] && <button type="button" data-testid={`icon-reset-${slug}`} onClick={reset} className="p-2 rounded-full text-slate-500 hover:bg-slate-100" title="Default"><ArrowCounterClockwise size={16} /></button>}
      <button type="button" data-testid={`icon-change-${slug}`} disabled={busy} onClick={() => ref.current?.click()}
        className="h-9 px-3 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center gap-1 hover:bg-slate-800 disabled:opacity-50">
        <UploadSimple size={14} /> {busy ? "..." : "Change"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
};

export function MaterialSymbolsDialog({ open, onClose }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { if (open) api.get("/materials-catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, [open]);
  const extra = catalog.filter((c) => !["Solar Panel", "Inverter", "Battery"].includes(c.name));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0" data-testid="material-symbols-dialog">
        <DialogHeader className="p-5 border-b border-slate-200">
          <DialogTitle>Material Symbols</DialogTitle>
          <DialogDescription>The image shown next to each material. Upload a better photo if one looks wrong.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto px-5 divide-y divide-slate-100">
          {CORE.map(([k, l]) => <IconRow key={k} k={k} label={l} />)}
          {extra.map((c) => <IconRow key={c.id} k={c.name} label={c.name} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
